import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, MaskLine, SectionLabel } from "./Reveal";
import { GALLERY } from "../data/site";

export const Work = () => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      data-testid="gallery-cuts-section"
      className="relative bg-[#0d1219] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <SectionLabel testId="work-label">Chapter 04 · The Work</SectionLabel>
        <h2 className="mt-7 max-w-3xl font-cond text-4xl font-extrabold uppercase leading-[0.92] sm:text-5xl lg:text-6xl">
          <MaskLine>Real chairs.</MaskLine>
          <MaskLine delay={0.1}>
            Real cuts.<span className="brass-text"> No filters.</span>
          </MaskLine>
        </h2>

        <div className="mt-14 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          {GALLERY.map((g, i) => {
            const wide = GALLERY.length % 2 === 1 && i === GALLERY.length - 1;
            return (
            <Reveal
              key={g.url}
              delay={Math.min(i * 0.055, 0.35)}
              className={wide ? "col-span-2 lg:col-span-1" : ""}
            >
              <button
                type="button"
                onClick={() => setActive(g)}
                data-testid={`gallery-item-${i}`}
                className="group relative block w-full overflow-hidden border border-white/[0.07] bg-[#0a0d12]"
              >
                <img
                  src={g.url}
                  srcSet={g.srcSet}
                  sizes={
                    wide
                      ? "(min-width: 1024px) 440px, 100vw"
                      : "(min-width: 1024px) 440px, 50vw"
                  }
                  alt={
                    g.alt ||
                    `${g.title} · ${g.tag} at The Vault Barber Co. in Argyle & Lantana TX`
                  }
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="500"
                  className={`w-full object-cover object-center grayscale-[0.35] transition-all duration-[900ms] ease-out group-hover:scale-[1.06] group-hover:grayscale-0 ${
                    wide ? "aspect-[16/11] lg:aspect-[4/5]" : "aspect-[4/5]"
                  }`}
                />
                <div className="absolute inset-0 bg-[#0a0d12]/30 transition-opacity duration-700 group-hover:opacity-0" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0a0d12] via-[#0a0d12]/70 to-transparent p-3 pt-10 sm:p-4">
                  <span className="label text-[9px]">{g.tag}</span>
                  <p className="mt-1 font-cond text-sm font-bold uppercase leading-tight tracking-wide text-[#f2ece0] sm:text-base">
                    {g.title}
                  </p>
                </div>
                <span className="pointer-events-none absolute inset-0 border border-[#c5a059]/0 transition-colors duration-500 group-hover:border-[#c5a059]/55" />
              </button>
            </Reveal>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <m.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <div className="absolute inset-0 bg-[#05070b]/94 backdrop-blur-sm" />
            <m.img
              src={active.url}
              alt={active.title}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 max-h-[86vh] max-w-full border border-white/10 object-contain"
            />
            <button
              type="button"
              data-testid="gallery-lightbox-close"
              aria-label="Close image"
              className="absolute right-5 top-5 z-20 flex h-11 w-11 items-center justify-center border border-white/15 text-slate-200"
            >
              <X size={18} />
            </button>
          </m.div>
        )}
      </AnimatePresence>
    </section>
  );
};
